/**
 * web/src/screens/Notebook/PhotosStrip.tsx — photos on a memory card
 * (epic lARMIX56PSIY, cards aWObthOOOhyI + aVk8uDpBPE4c).
 *
 * Spec: notes.jfarmer.xyz/#root/WSuVCMOCjbY0. Two sources, phone FIRST:
 *   1. this device — no OAuth, no Google account, works for Apple users;
 *   2. Google Photos — the picker, which sees only what the person taps.
 *
 * The strip lives on the card, under the words. It never auto-advances: these
 * are someone's photos, and a carousel that moves on its own is the wrong
 * register. One photo is a single tile — no dots, no counter.
 *
 * Spike note: bytes are served by the worker from the DO stand-in
 * (`photo_spike_blobs`) until the R2 card lands, so this component talks to
 * `/api/photos/*` and nothing else. Swapping storage does not touch it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, API_BASE, getToken } from '../../lib/api';
import { PartnerLabel } from '../../lib/partnerName';
import './photos.css';
interface Photo {
  id: string;
  url: string;
  bytes: number;
  mime: string;
  /** Who tapped it — 'me', or the partner's member id. Absent on rows that
   *  predate the stamp. */
  addedBy?: string | null;
}

interface Props {
  memoryId: string;
}

/** Mirror of the worker's PHOTOS_PER_MEMORY (worker/src/api/photos-picker.ts)
 *  — the strip's remaining-count copy and its disabled-at-cap state come from
 *  this number (PHtnmgVUAIkH). */
const PHOTOS_PER_MEMORY = 10;

/** 1600px longest edge, JPEG q0.82, EXIF dropped by the canvas round-trip.
 *  This is the difference between ~400KB and a 4MB phone original. */
async function shrink(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 1600 / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no canvas');
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();
  return canvas.toDataURL('image/jpeg', 0.82);
}

export function PhotosStrip({ memoryId }: Props) {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [sheet, setSheet] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [viewer, setViewer] = useState<number | null>(null);
  const [urls, setUrls] = useState<Record<string, string>>({});
  const fileRef = useRef<HTMLInputElement | null>(null);
  const cancelled = useRef(false);
  const objectUrls = useRef<string[]>([]);
  const remaining = Math.max(PHOTOS_PER_MEMORY - photos.length, 0);

  /** The media route is behind the couple token, so an <img src> cannot reach
   *  it: bytes are fetched with the header, then handed to the DOM as an
   *  object URL. No token in a URL, no public bucket. */
  useEffect(() => {
    let live = true;
    const token = getToken();
    (async () => {
      for (const p of photos) {
        if (urls[p.id]) continue;
        try {
          const res = await fetch(`${API_BASE}${p.url}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          if (!res.ok) continue;
          const blob = await res.blob();
          const objectUrl = URL.createObjectURL(blob);
          if (!live) {
            URL.revokeObjectURL(objectUrl);
            return;
          }
          objectUrls.current.push(objectUrl);
          setUrls((cur) => ({ ...cur, [p.id]: objectUrl }));
        } catch {
          /* a photo that will not load simply does not paint */
        }
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos]);

  useEffect(
    () => () => {
      objectUrls.current.forEach((u) => URL.revokeObjectURL(u));
      objectUrls.current = [];
    },
    [],
  );

  const load = useCallback(async () => {
    try {
      const res = await api<{ photos: Photo[] }>(
        `/api/photos/list?memoryId=${encodeURIComponent(memoryId)}`,
      );
      setPhotos(res.photos ?? []);
    } catch {
      /* a card with no photos is the normal case; say nothing */
    } finally {
      setLoading(false);
    }
  }, [memoryId]);

  useEffect(() => {
    void load();
    return () => {
      cancelled.current = true;
    };
  }, [load]);

  async function uploadFiles(files: FileList | null) {
    if (!files || !files.length) return;
    setSheet(false);
    setNote(null);
    for (const file of Array.from(files).slice(0, 10)) {
      try {
        const dataUrl = await shrink(file);
        const res = await api<{ photo: Photo }>('/api/photos/upload', {
          body: { memoryId, dataUrl },
        });
        setPhotos((cur) => [...cur, res.photo]);
      } catch {
        setNote('That photo could not be added.');
      }
    }
  }

  async function startGoogle() {
    // At the cap no picker opens at all — the add control is disabled, and
    // this guard keeps any stray call from creating a session anyway.
    if (remaining <= 0) return;
    setSheet(false);
    setNote(null);
    setWaiting(true);
    try {
      const status = await api<{ connected: boolean }>('/api/photos/status');
      if (!status.connected) {
        const res = await api<{ url: string }>('/api/photos/connect', {
          body: { return: window.location.origin },
        });
        // Consent is a full-page trip: the app returns with ?photos=connected
        // and the strip re-mounts, so nothing else is queued here.
        window.open(res.url, '_blank', 'noopener');
        setNote('Finish connecting Google Photos in the new tab, then come back.');
        setWaiting(false);
        return;
      }
      const session = await api<{ sessionId: string; pickerUri: string; pollIntervalMs: number; timeoutInMs: number }>(
        '/api/photos/picker/session',
        { body: { memoryId } },
      );
      if (!session.pickerUri || !session.sessionId) {
        setNote('Google did not open a picker. Try again in a moment.');
        setWaiting(false);
        return;
      }
      window.open(session.pickerUri, '_blank', 'noopener');
      const deadline = Date.now() + Math.min(session.timeoutInMs ?? 300000, 300000);
      while (Date.now() < deadline && !cancelled.current) {
        await new Promise((r) => setTimeout(r, Math.max(session.pollIntervalMs ?? 1500, 1000)));
        const poll = await api<{ mediaItemsSet: boolean }>(
          `/api/photos/picker/session?id=${encodeURIComponent(session.sessionId)}`,
        );
        if (poll.mediaItemsSet) {
          const collected = await api<{ photos: Photo[]; skipped: number; message?: string | null }>('/api/photos/picker/collect', {
            body: { sessionId: session.sessionId, memoryId },
          });
          setPhotos((cur) => [...cur, ...(collected.photos ?? [])]);
          // The worker names what was skipped (video, or a photo Google would
          // not hand over) — repeat its sentence rather than guessing.
          if (collected.message) setNote(collected.message);
          else if (collected.skipped > 0) setNote('Some of that pick could not be added.');
          setWaiting(false);
          return;
        }
      }
      setNote('Nothing came back from Google Photos.');
    } catch (e) {
      const msg = e instanceof Error ? e.message : '';
      setNote(
        /photos_not_connected/.test(msg)
          ? 'Connect Google Photos first.'
          : /photos_api_disabled/.test(msg)
            ? 'Google Photos is not switched on for this app yet.'
            : 'Could not reach Google Photos. Try the phone option instead.',
      );
    } finally {
      setWaiting(false);
    }
  }

  async function remove(id: string) {
    setPhotos((cur) => cur.filter((p) => p.id !== id));
    try {
      await api(`/api/photos/media/${id}`, { method: 'DELETE' });
    } catch {
      void load();
    }
  }

  if (loading) return null;

  const single = photos.length === 1;
  const index = viewer === null ? 0 : viewer;
  const current = photos[index];

  return (
    <div className="photo-strip" data-test="photo-strip">
      {photos.length > 0 && (
        <div className={`photo-frame${single ? ' is-single' : ''}`}>
          <button
            type="button"
            className="photo-open"
            aria-label={`Open photo ${index + 1}${single ? '' : ` of ${photos.length}`} full screen`}
            onClick={() => setViewer(index)}
          >
            <img
              src={urls[photos[index].id] ?? ''}
              alt=""
              loading="lazy"
              className="photo-img"
            />
          </button>
          {!single && (
            <div className="photo-dots" role="tablist" aria-label="Photos">
              {photos.map((p, i) => (
                <button
                  key={p.id}
                  type="button"
                  role="tab"
                  aria-selected={i === index}
                  aria-label={`Photo ${i + 1}`}
                  className={`photo-dot${i === index ? ' is-on' : ''}`}
                  onClick={() => setViewer(i)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="photo-row">
        {!single && photos.length > 1 && (
          <span className="photo-count">
            {index + 1} of {photos.length}
          </span>
        )}
        {remaining > 0 && (
          <span className="photo-remaining" data-test="photo-remaining">
            You can add {remaining} more photo{remaining === 1 ? '' : 's'}
          </span>
        )}
        {waiting ? (
          <span className="photo-waiting" data-test="photo-waiting">
            Waiting for Google Photos…
            <button type="button" className="photo-link" onClick={() => { cancelled.current = true; setWaiting(false); }}>
              Cancel
            </button>
          </span>
        ) : (
          <button
            type="button"
            className="photo-add"
            data-test="photo-add"
            disabled={remaining <= 0}
            onClick={() => setSheet(true)}
          >
            {remaining <= 0 ? 'This memory holds 10 photos. Remove one to add another.' : 'Add photos'}
          </button>
        )}
      </div>

      {note && <p className="photo-note" data-test="photo-note">{note}</p>}

      {sheet && (
        <div className="photo-sheet" role="dialog" aria-label="Add photos" data-test="photo-sheet">
          {/* Phone first: it needs no account and works for everyone. */}
          <button type="button" className="photo-source" onClick={() => fileRef.current?.click()}>
            <span className="photo-source-title">Take or choose a photo</span>
            <span className="photo-source-sub">From this phone or computer</span>
          </button>
          <button type="button" className="photo-source" onClick={() => void startGoogle()} data-test="photo-source-google">
            <span className="photo-source-title">Choose from Google Photos</span>
            <span className="photo-source-sub">{PartnerLabel()} will see the photos you pick</span>
          </button>
          <button type="button" className="photo-cancel" onClick={() => setSheet(false)}>Cancel</button>
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        onChange={(e) => void uploadFiles(e.target.files)}
      />

      {viewer !== null && current && (
        <div className="photo-viewer" role="dialog" aria-label="Photo" data-test="photo-viewer">
          <img src={urls[current.id] ?? ''} alt="" className="photo-viewer-img" />
          <div className="photo-viewer-bar">
            {photos.length > 1 && (
              <>
                <button type="button" className="photo-viewer-nav" aria-label="Previous photo" onClick={() => setViewer((index - 1 + photos.length) % photos.length)}>‹</button>
                <span className="photo-viewer-count">{index + 1} of {photos.length}</span>
                <button type="button" className="photo-viewer-nav" aria-label="Next photo" onClick={() => setViewer((index + 1) % photos.length)}>›</button>
              </>
            )}
            <button type="button" className="photo-viewer-remove" onClick={() => { void remove(current.id); setViewer(null); }}>
              Remove photo
            </button>
            <button type="button" className="photo-viewer-close" onClick={() => setViewer(null)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}
